import React, { useMemo, useState } from 'react';

import { Radio, Skeleton, Tabs } from 'antd';

import Chart from 'components/Chart';
import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import Spin from 'components/Spin';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import { useLastGains } from 'v2/hooks/useLastGains';

type BarChartProps = {
  id: string;
};

type BarDataset = { data: number[]; label: string; backgroundColor: string };
type BarChartData = { labels: string[]; datasets: BarDataset[] };

enum DateType {
  PAYMENT = '1',
  CREATION = '2'
}

enum TabKeys {
  PRODUCER = '0',
  COPRODUCER = '1',
  AFFILIATE = '2'
}

function stringToHue(text: string) {
  let hash = 0;

  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }

  return Math.abs(hash) % 360;
}

function hslToHex(h: number, s: number, l: number) {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (x: number) =>
    Math.round(255 * x)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

function getColorForLabel(label: string) {
  const hue = stringToHue(label || 'dataset');
  return hslToHex(hue, 65, 55);
}

const options = {
  responsive: true,
  legend: {
    display: false
  },
  scales: {
    xAxes: [{ stacked: true }],
    yAxes: [{ stacked: true }]
  }
};

const BarChart = ({ id }: BarChartProps) => {
  const [tab, setTab] = useState<string>(TabKeys.PRODUCER);
  const [dateType, setDateType] = useState<DateType>(DateType.PAYMENT);
  const { lastGains, isLoading } = useLastGains();

  const source = useMemo(() => {
    switch (tab) {
      case TabKeys.COPRODUCER:
        return lastGains.coproducer;
      case TabKeys.AFFILIATE:
        return lastGains.affiliate;
      default:
        return lastGains.producer;
    }
  }, [tab, lastGains]);

  const rawSection = useMemo(() => {
    if (!source) return null;
    return dateType === DateType.CREATION ? source.create : source.payment;
  }, [source, dateType]);

  const graphData: BarChartData | null = useMemo(() => {
    if (!rawSection?.labels || !rawSection?.datasets) return null;

    const coloredDatasets: BarDataset[] = (rawSection.datasets || []).map((item: any) => ({
      data: item.data,
      label: item.label,
      backgroundColor: getColorForLabel(String(item.label || 'dataset'))
    }));

    return {
      labels: rawSection.labels || [],
      datasets: coloredDatasets
    };
  }, [rawSection]);

  const hasData = !!graphData?.labels?.length;

  const totalPeriod = useMemo(() => {
    return (graphData?.datasets || []).reduce(
      (sum: number, ds: BarDataset) => sum + ds.data.reduce((a: number, b: number) => a + (b || 0), 0),
      0
    );
  }, [graphData]);

  const totalPerDay = (index: number) => {
    const dayValues = (graphData?.datasets || []).map((item: BarDataset) => item.data[index] || 0);
    return dayValues.reduce((sum: number, current: number) => sum + current, 0);
  };

  return (
    <div className='h-full rounded-sm bg-white shadow-sm'>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className='flex flex-wrap items-center justify-between gap-2 p-4'>
          <div>
            <h4 className='m-0 text-lg font-semibold'>Ganho últimos 7 dias</h4>
            <span className='text-sm text-gray-500'>
              Total no período:{' '}
              {isLoading ? (
                <Skeleton.Input active size='small' style={{ width: 80 }} />
              ) : (
                <Mask>
                  <ColoredFormattedNumber value={totalPeriod} />
                </Mask>
              )}
            </span>
          </div>
          <Radio.Group size='small' value={dateType} onChange={e => setDateType(e.target.value)}>
            <Radio.Button value={DateType.PAYMENT}>Pagamento</Radio.Button>
            <Radio.Button value={DateType.CREATION}>Criação</Radio.Button>
          </Radio.Group>
        </div>

        <Tabs
          activeKey={tab}
          onChange={key => setTab(key)}
          centered
          items={[
            { key: TabKeys.PRODUCER, label: 'Produtor', disabled: isLoading },
            { key: TabKeys.COPRODUCER, label: 'Coprodutor', disabled: isLoading },
            { key: TabKeys.AFFILIATE, label: 'Afiliado', disabled: isLoading }
          ]}
        />

        <div className='h-full p-4'>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Spin />
            </div>
          ) : hasData ? (
            <div>
              <div className='h-[220px]'>
                <Chart id={id} type='bar' options={options} data={graphData} />
              </div>
              <div className='mt-2'>
                {graphData!.labels.map((item, index) => (
                  <div key={item} className='mb-1 flex w-full justify-between text-sm'>
                    <span>{item}</span>
                    <Mask>
                      <ColoredFormattedNumber value={totalPerDay(index)} />
                    </Mask>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>Não há dados a serem mostrados</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BarChart;
